import { useEffect, useState } from 'react'
import {
  Autocomplete,
  Button,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material'
import { useAuthContext } from '../../../hooks/useAuthContext'
import { fieldTemplateService } from '../../../services/fieldTemplateService'
import {
  COMPUTED_OPERATION_TYPES,
  COMPUTED_RESERVED_KEYS,
  DYNAMIC_FIELD_TYPE,
  FIELD_TYPES,
} from '../../../utility/constants'
import { listDataService } from '../../../services/listDataService'
import { toast } from 'react-toastify'
import DeleteIcon from '@mui/icons-material/Delete'
import { GroupHeader, GroupItems } from './CreateProcessTemplateDialog'
import AlternateComputationSetupUI from './AlternateComputationSetupUI'
import { getTimeInMillis } from '../../../utility/datetime'
import { activityTemplateService } from '../../../services/activityTemplateService'

export const PARAM_TYPES = {
  Field: '^^',
  Keyword: '**',
  Constant: '',
}

export const VALUE_POLARITY = {
  '+ve': '+',
  '-ve': '-',
}

export const PROPERTIES = {
  value: 'value',
  id: 'id',
}

export const getPropertyOptions = (selectedField, lists = []) => {
  // Start with default properties
  const options = { ...PROPERTIES }
  // If the selected field is a list or multi-list type, add metadata keys from the list
  if (
    (selectedField?.data_structure === FIELD_TYPES.LIST ||
      selectedField?.data_structure === FIELD_TYPES.LIST_MULTI) &&
    selectedField?.list_id
  ) {
    const list = lists?.find((l) => l.id == selectedField.list_id)
    if (list?.metadata_keys) {
      const metadataKeys = list.metadata_keys.split(',').map((k) => k.trim())
      metadataKeys.forEach((key) => {
        if (key) {
          options[key] = key
        }
      })
    }
  } else if (
    (selectedField?.data_structure === FIELD_TYPES.LIST ||
      selectedField?.data_structure === FIELD_TYPES.LIST_MULTI) &&
    selectedField?.list_id == null &&
    selectedField?.dynamic_field_type
  ) {
    // Dynamic fields (users/teams): expose their well-known metadata keys
    const dynType = selectedField.dynamic_field_type?.type
    if (dynType === DYNAMIC_FIELD_TYPE.USERS) {
      ;['email', 'employee_id', 'first_name', 'last_name', 'designation', 'department'].forEach(
        (k) => {
          options[k] = k
        },
      )
    } else if (dynType === DYNAMIC_FIELD_TYPE.TEAMS) {
      ;['team_type', 'team_subtype', 'team_name'].forEach((k) => {
        options[k] = k
      })
    }
  }
  return options
}

export const getKeyOfValue = (obj = {}, val) => {
  const key = Object.entries(obj)?.find(([key, value]) => {
    return value == val
  })

  if (key) {
    return key[0]
  } else {
    return undefined
  }
}

export const getItemFromList = (list, key, value) => {
  return list?.find((it) => it[key] == value)
}

const getParamsFromMetadata = (metadata, fields = []) => {
  const params = []
  const operation = metadata?.operation
  metadata?.params?.forEach((param, index) => {
    if (param?.toString()?.startsWith('^^')) {
      params.push(
        [COMPUTED_OPERATION_TYPES.MULTIPLY, COMPUTED_OPERATION_TYPES.SUM].includes(operation)
          ? {
              id: new Date().getTime() + index,
              type: 'Field',
              field: getItemFromList(fields, 'id', param.substring(3, param.indexOf('.'))),
              polarity: getKeyOfValue(VALUE_POLARITY, param.substring(2, 3)),
              property: param.substring(param.indexOf('.') + 1),
            }
          : {
              id: new Date().getTime() + index,
              type: 'Field',
              field: getItemFromList(fields, 'id', param.substring(2, param.indexOf('.'))),
              polarity: '',
              property: param.substring(param.indexOf('.') + 1),
            },
      )
    } else if (param?.toString()?.startsWith('**')) {
      params.push({
        id: new Date().getTime() + index,
        type: 'Keyword',
        field: getKeyOfValue(COMPUTED_RESERVED_KEYS, param.substring(2)),
      })
    } else {
      params.push({
        id: new Date().getTime() + index,
        type: 'Constant',
        field: param,
      })
    }
  })
  return params
}

export const getParamField = (param, operation) => {
  if (param.type == 'Field') {
    return (
      '' +
      PARAM_TYPES[param.type] +
      ([COMPUTED_OPERATION_TYPES.MULTIPLY, COMPUTED_OPERATION_TYPES.SUM].includes(operation)
        ? VALUE_POLARITY[param.polarity] || '+'
        : '') +
      param?.field?.id +
      '.' +
      param?.property
    )
  } else if (param.type == 'Keyword') {
    return '' + PARAM_TYPES[param.type] + COMPUTED_RESERVED_KEYS[param?.field]
  } else {
    return '' + PARAM_TYPES[param.type] + param?.field
  }
}

const getExclusionsFromMetadata = (metadata, fields = [], listData) => {
  const exclusions = []
  metadata?.exclusions?.forEach((exclusion, index) => {
    const f = fields.find((it) => it.id == exclusion.field_template_id)
    exclusions.push({
      id: new Date().getTime() + index,
      field: f,
      listItem: listData[`list_data_${f?.list_id}`]?.find((it) => it.id == exclusion.list_data_id),
    })
  })
  return exclusions
}

const FieldComputationSetupUI = ({ field, activityTemplateId, processTemplateId, saveChanges }) => {
  const { user } = useAuthContext()
  const [operation, setOperation] = useState('')
  const [computed, setComputed] = useState(field.is_computed)
  const [pivotFieldId, setPivotFieldId] = useState(null)
  const [counterFieldId, setCounterFieldId] = useState(null)
  const [exclusions, setExclusions] = useState([])
  const [params, setParams] = useState([])
  const [altFieldTemplateId, setaltFieldTemplateId] = useState(null)
  const [fields, setFields] = useState([])
  const [allPivotFields, setAllPivotFields] = useState([])
  const [listData, setListData] = useState([])
  const [lists, setLists] = useState([])
  const [altComputation, setaltComputation] = useState([])
  const [selectDateComputationCategory, setSelectDateComputationCategory] = useState(null)
  const [dateSourceType, setDateSourceType] = useState(null)
  const [activities, setActivities] = useState(null)
  const [selectedActivity, setSelectedActivity] = useState(null)
  const [version, setVersion] = useState('')
  const [dateFields, setDateFields] = useState([])
  const [selectedDateField, setSelectedDateField] = useState(null)
  const [durationCategoryType, setDurationCategoryType] = useState(null)
  const [numericFields, setNumericFields] = useState([])
  const [selectedNumericField, setSelectedNumericField] = useState(null)
  const [activityListFields, setActivityListFields] = useState([])
  const [selectedListField, setSelectedListField] = useState(null)
  const [selectedListMetaDataKey, setSelectedListMetaDataKey] = useState(null)
  const [submitted, setSubmitted] = useState(false)

  useEffect(() => {
    ;(async () => {
      if (user && activityTemplateId) {
        // get all fields
        const allFieldResult = await fieldTemplateService.getDerivableFieldsForProcess(
          user,
          processTemplateId,
        )
        setFields(allFieldResult)

        const allPivotFields = await fieldTemplateService.getAllPivotFieldsInProcess(
          user,
          processTemplateId,
        )
        setAllPivotFields(allPivotFields?.data || [])

        // get all list items in a activity
        const listData = await listDataService.getDataActivity(user, activityTemplateId)
        setListData(listData)

        // get all lists to access metadata_keys for property options
        const allLists = await listDataService.getListOfListsForAdmin(user)
        setLists(allLists)

        const numericMetadataListIds = new Set()

        Object.values(listData).forEach((list) => {
          list.forEach((item) => {
            if (item.metadata) {
              Object.entries(item.metadata).forEach(([_, value]) => {
                if (!isNaN(Number(value))) {
                  numericMetadataListIds.add(item.list_id)
                }
              })
            }
          })
        })

        const activitiesForProcess = await activityTemplateService.getActivityForProcess(
          user,
          processTemplateId,
        )
        setActivities(activitiesForProcess.data)

        const fieldsForActivity = (
          await fieldTemplateService.getAllFieldsInActivity(user, activityTemplateId)
        ).data

        const dateFields = fieldsForActivity.filter((df) => df.data_structure === FIELD_TYPES.DATE)
        setDateFields(dateFields)

        const listFields = fieldsForActivity.filter(
          (df) => df.data_structure === FIELD_TYPES.LIST && numericMetadataListIds.has(df.list_id),
        )
        setActivityListFields(listFields)

        const numericFields = fieldsForActivity.filter(
          (df) => df.data_structure === FIELD_TYPES.NUMBER,
        )
        setNumericFields(numericFields)
      }
    })()
  }, [field])

  useEffect(() => {
    // convert metadata in representation format
    const operationKey = getKeyOfValue(COMPUTED_OPERATION_TYPES, field?.metadata?.operation)

    const resolvedOperation = COMPUTED_OPERATION_TYPES[operationKey]
    setOperation(resolvedOperation)

    if (fields) {
      const defaultParams = getParamsFromMetadata(field?.metadata, fields)
      setParams(defaultParams)
    }

    if (fields && listData) {
      const defaultExclusions = getExclusionsFromMetadata(field?.metadata, fields, listData)
      setExclusions(defaultExclusions)
    }

    if (field?.metadata?.counterFieldTemplateId || field?.metadata?.counterFieldId) {
      setCounterFieldId(
        getItemFromList(
          fields,
          'id',
          field?.metadata?.counterFieldTemplateId || field?.metadata?.counterFieldId,
        )?.id,
      )
    }

    if (field?.metadata?.pivotFieldTemplateId) {
      setPivotFieldId(getItemFromList(fields, 'id', field?.metadata?.pivotFieldTemplateId)?.id)
    }

    if (field?.metadata?.alt_field_template_id) {
      setaltFieldTemplateId(
        getItemFromList(fields, 'id', field?.metadata?.alt_field_template_id)?.id,
      )

      setaltComputation(field?.metadata?.alt || [])
    }

    if (
      resolvedOperation === COMPUTED_OPERATION_TYPES.DATE_COMPUTATION &&
      field?.metadata?.date_config
    ) {
      const { source_date, duration, date_computation_category } = field.metadata.date_config

      //SourceDate Calculation
      if (source_date?.date_computation_source_type) {
        setDateSourceType(source_date.date_computation_source_type)

        if (
          source_date.date_computation_source_type === 'activity' &&
          source_date.activity_template_id &&
          activities
        ) {
          const activity = activities.find((a) => a.id === source_date.activity_template_id)
          setSelectedActivity(activity || null)
          setVersion(source_date.version_type || null)
        }

        if (
          source_date.date_computation_source_type === 'field' &&
          source_date.field_template_id &&
          dateFields
        ) {
          const dateField = dateFields.find((f) => f.id === source_date.field_template_id)
          setSelectedDateField(dateField || null)
        }
      }

      if (date_computation_category) {
        setSelectDateComputationCategory(date_computation_category)
      }

      //Duration Calculation
      if (duration?.duration_category_type) {
        setDurationCategoryType(duration.duration_category_type)

        if (
          duration.duration_category_type === 'field' &&
          duration.numeric_field_template_id &&
          numericFields
        ) {
          const numericField = numericFields.find(
            (f) => f.id === duration.numeric_field_template_id,
          )
          setSelectedNumericField(numericField || null)
        }

        if (
          duration.duration_category_type === 'list' &&
          duration.list_field_template_id &&
          activityListFields
        ) {
          const listField = activityListFields.find((f) => f.id === duration.list_field_template_id)
          setSelectedListField(listField || null)
          setSelectedListMetaDataKey(duration.list_metadata_key || null)
        }
      }
    }
  }, [field, fields, listData, activities, dateFields, numericFields, activityListFields])

  const getSelectedField = (fieldId, sourceFields = fields) => {
    const field = sourceFields?.find((it) => it.id == fieldId)
    if (field) {
      return { value: field, label: field?.field_template_name }
    } else {
      return null
    }
  }

  const getSelectedItem = (itemId, listId) => {
    const items = listData[`list_data_${listId}`]
    const item = items?.find((it) => it.id == itemId)
    if (item) {
      return { value: item, label: item?.value }
    } else {
      return null
    }
  }

  const getListItemOptions = (field) => {
    if (field) {
      const items = listData[`list_data_${field?.list_id}`]
      if (items) {
        return items.map((it) => ({ value: it, label: it.value }))
      } else {
        return []
      }
    } else {
      return []
    }
  }

  const addParam = () => {
    setParams([
      ...params,
      {
        id: getTimeInMillis(),
      },
    ])
  }

  const addAltComputation = () => {
    const updated = [
      ...altComputation,
      {
        id: getTimeInMillis(),
      },
    ]

    setaltComputation(updated)
  }

  const addExclusion = () => {
    setExclusions([
      ...exclusions,
      {
        id: getTimeInMillis(),
      },
    ])
  }

  const saveComputation = async () => {
    try {
      if (operation === COMPUTED_OPERATION_TYPES.DATE_COMPUTATION) {
        if (!selectDateComputationCategory) {
          toast.error('Please select a Day Type.')
          return
        }
        if (!dateSourceType) {
          toast.error('Please select a Date Source.')
          return
        }
        if (!durationCategoryType) {
          toast.error('Please select a Duration Source.')
          return
        }
      }

      if (dateSourceType === 'activity') {
        if (!selectedActivity) {
          toast.error('Please select the required activity.')
          return
        }
        if (!version) {
          toast.error('Please select the required activity version.')
          return
        }
      }

      if (dateSourceType === 'field') {
        if (!selectedDateField) {
          toast.error('Please select the required field.')
          return
        }
      }

      if (durationCategoryType === 'list') {
        if (!selectedListField) {
          toast.error('Please select the required list.')
          return
        }
      }

      if (durationCategoryType === 'field') {
        if (!selectedNumericField) {
          toast.error('Please select the required field.')
          return
        }
      }

      const finalParams = params.map((it) => getParamField(it, operation))

      const source_date = {
        date_computation_source_type: dateSourceType || null,
      }

      if (dateSourceType === 'activity' && selectedActivity?.id) {
        source_date.activity_template_id = selectedActivity.id
        source_date.version_type = version
      }

      if (dateSourceType === 'field' && selectedDateField?.id) {
        source_date.field_template_id = selectedDateField.id
      }

      const duration = {
        duration_category_type: durationCategoryType || null,
      }

      if (durationCategoryType === 'field' && selectedNumericField?.id) {
        duration.numeric_field_template_id = selectedNumericField.id
      }

      if (durationCategoryType === 'list' && selectedListField?.id) {
        duration.list_field_template_id = selectedListField.id
        duration.list_metadata_key = selectedListMetaDataKey || null
      }

      const date_computation_category = selectDateComputationCategory || null

      const dateConfig =
        operation === COMPUTED_OPERATION_TYPES.DATE_COMPUTATION
          ? {
              source_date,
              duration,
              date_computation_category,
            }
          : null

      const finalComputationValue = {
        alt: altComputation,
        alt_field_template_id: altFieldTemplateId,
        operation: operation,
        pivotFieldTemplateId: pivotFieldId,
        counterFieldTemplateId: counterFieldId,
        params: finalParams,
        exclusions: exclusions.map((it) => ({
          field_template_id: it?.field?.id,
          list_data_id: it?.listItem?.id,
        })),
        date_config: dateConfig,
      }

      const updatedField = {
        ...field,
        metadata: computed ? finalComputationValue : null,
        is_computed: computed,
        is_read_only: computed,
      }

      saveChanges(updatedField)
      toast.success('Computation added successfully!')
    } catch (error) {
      toast.error(error.message)
    }
  }

  const currentActivityFields = fields.filter((it) => it.activity_template_id == activityTemplateId)

  const getParamFieldOptions = (currentOperation) => {
    const sourceFields =
      currentOperation === COMPUTED_OPERATION_TYPES.SUM_ACROSS_PROCESS ?  fields : currentActivityFields 

    return [...sourceFields].sort((a, b) =>
      -(b?.activity_template_name || '').localeCompare(a?.activity_template_name || ''),
    )
  }

  return computed ? (
    <div>
      {/* Is Computed */}
      <FormControl variant="outlined" size="small" color="primary" fullWidth>
        <InputLabel id="select-option-label">Is Computed</InputLabel>
        <Select
          style={{ minWidth: 60, marginBottom: 16 }}
          size="small"
          color="primary"
          labelId="select-option-label"
          id="select-option"
          value={computed ? 'Yes' : 'No'}
          onChange={(e) => {
            const isComputed = e.target.value == 'Yes'
            setComputed(isComputed)
          }}
          label="Polarity"
        >
          <MenuItem value={'No'}>No</MenuItem>
          <MenuItem value={'Yes'}>Yes</MenuItem>
        </Select>
      </FormControl>

      {/* OPERATION */}
      <FormControl variant="outlined" size="small" color="primary" fullWidth>
        <InputLabel id="select-option-label">Operation</InputLabel>
        <Select
          style={{ minWidth: '100%', marginBottom: 16 }}
          size="small"
          color="primary"
          labelId="select-option-label"
          id="select-option"
          value={operation}
          onChange={(e) => {
            setOperation(e.target.value)
            if (e.target.value != COMPUTED_OPERATION_TYPES.SUM_ACROSS_PROCESS) {
              setExclusions([])
            }

            if (
              ![COMPUTED_OPERATION_TYPES.MULTIPLY, COMPUTED_OPERATION_TYPES.SUM].includes(
                e.target.value,
              )
            ) {
              setParams(params.map((it) => ({ ...it, polarity: '' })))
            }
          }}
          label="Operation"
        >
          {Object.keys(COMPUTED_OPERATION_TYPES).map((it) => {
            return <MenuItem value={COMPUTED_OPERATION_TYPES[it]}>{it}</MenuItem>
          })}
        </Select>
      </FormControl>

      {/*Date Computation Types */}
      {operation === COMPUTED_OPERATION_TYPES.DATE_COMPUTATION && (
        <FormControl variant="outlined" size="small" color="primary" fullWidth>
          <InputLabel id="date-computation-types">Day Type</InputLabel>
          <Select
            style={{ minWidth: 60, marginBottom: 16 }}
            size="small"
            color="primary"
            labelId="date-computation-types"
            id="date-computation-types"
            value={selectDateComputationCategory || ''}
            onChange={(e) => setSelectDateComputationCategory(e.target.value)}
            label="Day Type"
            error={submitted && !selectDateComputationCategory}
          >
            <MenuItem value={'working-days'}>Working Days</MenuItem>
            <MenuItem value={'calender-days'}>Calender Days</MenuItem>
          </Select>
        </FormControl>
      )}

      {/*Counter - Pivot Field */}
      {params.find((it) => it.field == 'COUNTER') && (
        <Autocomplete
          style={{ marginBottom: 16 }}
          value={getSelectedField(
            pivotFieldId || null,
            currentActivityFields.filter((it) => it.is_pivot_field),
          )}
          id="autocomplete-field"
          options={currentActivityFields
            .filter((it) => it.is_pivot_field)
            .map((it) => {
              return { value: it, label: it.field_template_name }
            })}
          sx={{ minWidth: 160 }}
          renderInput={(params) => <TextField {...params} label="Select Pivot Field" />}
          onChange={(_, v) => {
            setPivotFieldId(v?.value?.id)
          }}
          size="small"
        />
      )}

      {/*Sum Across Process Pivot Field */}
      {operation == COMPUTED_OPERATION_TYPES.SUM_ACROSS_PROCESS && (
        <Autocomplete
          style={{ marginBottom: 16 }}
          value={getSelectedField(pivotFieldId || null)}
          id="sum-across-pivot-field"
          options={allPivotFields.map((it) => {
            return { value: it, label: it.field_template_name }
          })}
          sx={{ minWidth: 160 }}
          renderInput={(params) => <TextField {...params} label="Select Pivot Field" />}
          onChange={(_, v) => {
            setPivotFieldId(v?.value?.id)
          }}
          size="small"
        />
      )}

      {operation === COMPUTED_OPERATION_TYPES.DATE_COMPUTATION && (
        <>
          <FormControl
            variant="outlined"
            size="small"
            color="primary"
            fullWidth
            style={{ marginBottom: 16 }}
          >
            <InputLabel id="computation-category-label">Select Date Source</InputLabel>
            <Select
              labelId="computation-category-label"
              id="computation-category"
              value={dateSourceType || ''}
              onChange={(e) => {
                setDateSourceType(e.target.value)
              }}
              label="Select Date Source"
              error={submitted && !dateSourceType}
            >
              <MenuItem value={'activity'}>Activity</MenuItem>
              <MenuItem value={'field'}>Field</MenuItem>
            </Select>
          </FormControl>
          {dateSourceType === 'activity' && activities && (
            <FormControl
              variant="outlined"
              size="small"
              color="primary"
              fullWidth
              style={{ marginBottom: 16 }}
            >
              <InputLabel id="activity-label">Select Activity</InputLabel>
              <Select
                labelId="activity-label"
                id="activity-select"
                value={selectedActivity?.id || ''}
                onChange={(e) => {
                  const activity = activities.find((act) => act.id === e.target.value)
                  setSelectedActivity(activity)
                }}
                label="Select Activity"
              >
                {activities.map((activity) => (
                  <MenuItem key={activity.id} value={activity.id}>
                    {activity.activity_template_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {dateSourceType === 'activity' && activities && (
            <FormControl variant="outlined" size="small" color="primary" fullWidth>
              <InputLabel id="version-options-label">Select Version</InputLabel>
              <Select
                labelId="version-options-label"
                id="version-options"
                value={version || ''}
                onChange={(e) => setVersion(e.target.value)}
                label="Select Version"
                style={{ minWidth: '100%', marginBottom: 16 }}
              >
                <MenuItem value="initial-version">Initial Version</MenuItem>
                <MenuItem value="latest-version">Latest Version</MenuItem>
              </Select>
            </FormControl>
          )}

          {dateSourceType === 'field' && dateFields && (
            <FormControl
              variant="outlined"
              size="small"
              color="primary"
              fullWidth
              style={{ marginBottom: 16 }}
            >
              <InputLabel id="field-label">Select Field</InputLabel>
              <Select
                labelId="field-label"
                id="field-select"
                value={selectedDateField?.id || ''}
                onChange={(e) => {
                  const field = dateFields.find((df) => df.id === e.target.value)
                  setSelectedDateField(field)
                }}
                label="Select Field"
              >
                {dateFields.map((field) => (
                  <MenuItem key={field.id} value={field.id}>
                    {field.field_template_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <FormControl
            variant="outlined"
            size="small"
            color="primary"
            fullWidth
            style={{ marginBottom: 16 }}
          >
            <InputLabel id="duration-category">Select Duration Source</InputLabel>
            <Select
              labelId="duration-category"
              id="duration-category"
              value={durationCategoryType || ''}
              onChange={(e) => setDurationCategoryType(e.target.value)}
              label="Select Duration Source"
              error={submitted && !durationCategoryType}
            >
              <MenuItem value={'list'}>List</MenuItem>
              <MenuItem value={'field'}>Field</MenuItem>
            </Select>
          </FormControl>
          {durationCategoryType === 'field' && (
            <FormControl
              variant="outlined"
              size="small"
              color="primary"
              fullWidth
              style={{ marginBottom: 16 }}
            >
              <InputLabel id="number-field-label">Select Numeric Field</InputLabel>
              <Select
                labelId="number-field-label"
                id="number-field-select"
                value={selectedNumericField?.id || ''}
                onChange={(e) => {
                  const field = numericFields.find((df) => df.id === e.target.value)
                  setSelectedNumericField(field)
                }}
                label="Select List Field"
              >
                {numericFields.map((field) => (
                  <MenuItem key={field.id} value={field.id}>
                    {field.field_template_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          {durationCategoryType === 'list' && activityListFields.length > 0 && (
            <FormControl
              variant="outlined"
              size="small"
              color="primary"
              fullWidth
              style={{ marginBottom: 16 }}
            >
              <InputLabel id="list-field-label">Select List Field</InputLabel>
              <Select
                labelId="list-field-label"
                id="list-field-select"
                value={selectedListField?.id || ''}
                onChange={(e) => {
                  const field = activityListFields.find((df) => df.id === e.target.value)
                  setSelectedListField(field)
                  setSelectedListMetaDataKey(null)
                }}
                label="Select List Field"
              >
                {activityListFields.map((field) => (
                  <MenuItem key={field.id} value={field.id}>
                    {field.field_template_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          {durationCategoryType === 'list' &&
            activityListFields.length > 0 &&
            selectedListField && (
              <FormControl
                variant="outlined"
                size="small"
                color="primary"
                fullWidth
                style={{ marginBottom: 16 }}
              >
                <InputLabel id="list-metadata-label">Select List Meta Data</InputLabel>
                <Select
                  labelId="list-metadata-label"
                  id="list-metadata-select"
                  value={selectedListMetaDataKey || ''}
                  onChange={(e) => setSelectedListMetaDataKey(e.target.value)}
                  label="Select List Meta Data"
                >
                  {(
                    lists
                      ?.find((l) => l.id === selectedListField?.list_id)
                      ?.metadata_keys?.split(',')
                      ?.map((k) => k.trim())
                      ?.filter(Boolean) || []
                  ).map((key) => (
                    <MenuItem key={key} value={key}>
                      {key}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
        </>
      )}

      {params.find((it) => it.field == 'COUNTER') && (
        <Autocomplete
          style={{ marginBottom: 20 }}
          value={getSelectedField(counterFieldId || null, currentActivityFields)}
          id="autocomplete-field"
          options={currentActivityFields.map((it) => {
            return { value: it, label: it.field_template_name }
          })}
          sx={{ minWidth: 160 }}
          renderInput={(params) => <TextField {...params} label="Select Counter Field" />}
          onChange={(_, v) => {
            setCounterFieldId(v?.value?.id)
          }}
          size="small"
        />
      )}
      <p>Params: </p>
      {params.map((param, index) => {
        return (
          <div style={{ display: 'flex', columnGap: 8, marginBottom: 16, alignItems: 'center' }}>
            <p style={{ margin: 0, minWidth: 20 }}>{index + 1}.</p>
            {/* PARAM TYPE */}
            <FormControl variant="outlined" size="small" color="primary">
              <InputLabel id="select-option-label">Param Type</InputLabel>
              <Select
                style={{ minWidth: 120 }}
                size="small"
                color="primary"
                labelId="select-option-label"
                id="select-option"
                value={param.type}
                onChange={(e) => {
                  setParams(
                    params.map((it) => {
                      return it.id == param.id ? { ...param, type: e.target.value } : it
                    }),
                  )
                }}
                label="Param Type"
              >
                {Object.keys(PARAM_TYPES).map((it) => {
                  return <MenuItem value={it}>{it}</MenuItem>
                })}
              </Select>
            </FormControl>
            {/* FIELD */}
            {param.type == 'Field' ? (
              <Autocomplete
                sx={{ minWidth: 160 }}
                id="select-param-field"
                options={getParamFieldOptions(operation)}
                groupBy={(option) => option?.activity_template_name}
                size="small"
                getOptionLabel={(item) => item?.field_template_name}
                value={param?.field || null}
                onChange={(_, v) => {
                  setParams(
                    params.map((it) => {
                      return it.id == param.id ? { ...param, field: v } : it
                    }),
                  )
                }}
                renderGroup={(params) => (
                  <li key={params.key}>
                    <GroupHeader>{params.group}</GroupHeader>
                    <GroupItems>{params.children}</GroupItems>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    label="Select derived field"
                    color="primary"
                  />
                )}
              />
            ) : param.type == 'Keyword' ? (
              <FormControl variant="outlined" size="small" color="primary">
                <InputLabel id="select-option-label">Keyword</InputLabel>
                <Select
                  style={{ minWidth: 160 }}
                  size="small"
                  color="primary"
                  labelId="select-option-label"
                  id="select-option"
                  value={param.field}
                  onChange={(e) => {
                    setParams(
                      params.map((it) => {
                        return it.id == param.id ? { ...param, field: e.target.value } : it
                      }),
                    )
                  }}
                  label="Keyword"
                >
                  {Object.keys(COMPUTED_RESERVED_KEYS).map((it) => {
                    return <MenuItem value={it}>{it}</MenuItem>
                  })}
                </Select>
              </FormControl>
            ) : (
              <TextField
                size="small"
                value={param.field}
                label="Static Value"
                onChange={(e) => {
                  setParams(
                    params.map((it) => {
                      return it.id == param.id ? { ...param, field: e.target.value } : it
                    }),
                  )
                }}
              />
            )}
            {/* Field Properties */}
            {param.type == 'Field' && param.field && (
              <FormControl variant="outlined" size="small" color="primary">
                <InputLabel id="select-option-label">Property</InputLabel>
                <Select
                  style={{ minWidth: 100 }}
                  size="small"
                  color="primary"
                  labelId="select-option-label"
                  id="select-option"
                  value={param.property}
                  onChange={(e) => {
                    setParams(
                      params.map((it) => {
                        return it.id == param.id ? { ...param, property: e.target.value } : it
                      }),
                    )
                  }}
                  label="Property"
                >
                  {Object.keys(getPropertyOptions(param.field, lists)).map((it) => {
                    return <MenuItem value={it}>{it}</MenuItem>
                  })}
                </Select>
              </FormControl>
            )}
            {/* Positive or Negative */}
            {param.type == 'Field' &&
              param.field &&
              (operation == COMPUTED_OPERATION_TYPES.SUM ||
                operation == COMPUTED_OPERATION_TYPES.MULTIPLY) && (
                <FormControl variant="outlined" size="small" color="primary">
                  <InputLabel id="select-option-label">Polarity</InputLabel>
                  <Select
                    style={{ minWidth: 60 }}
                    size="small"
                    color="primary"
                    labelId="select-option-label"
                    id="select-option"
                    value={param.polarity || '+ve'}
                    onChange={(e) => {
                      setParams(
                        params.map((it) => {
                          return it.id == param.id ? { ...param, polarity: e.target.value } : it
                        }),
                      )
                    }}
                    label="Polarity"
                  >
                    {Object.keys(VALUE_POLARITY).map((it) => {
                      return <MenuItem value={it}>{it}</MenuItem>
                    })}
                  </Select>
                </FormControl>
              )}
            <IconButton
              onClick={() => {
                setParams(params.filter((it) => it.id != param.id))
              }}
            >
              <DeleteIcon size="small" />
            </IconButton>
          </div>
        )
      })}

      {operation == COMPUTED_OPERATION_TYPES.SUM_ACROSS_PROCESS && (
        <>
          <p>Exclusions: </p>
          {exclusions.map((exclusion, index) => {
            return (
              <div
                style={{ display: 'flex', columnGap: 8, marginBottom: 16, alignItems: 'center' }}
              >
                <p style={{ margin: 0, minWidth: 20 }}>{index + 1}.</p>
                {/* Excluded Field */}
                <Autocomplete
                  value={getSelectedField(exclusion?.field?.id || null)}
                  id="autocomplete-field"
                  options={fields
                    .filter((it) => it.data_structure == FIELD_TYPES.LIST)
                    .map((it) => {
                      return { value: it, label: it.field_template_name }
                    })}
                  sx={{ minWidth: 160 }}
                  renderInput={(params) => <TextField {...params} label="Excluded Field" />}
                  onChange={(e, v) => {
                    setExclusions(
                      exclusions.map((it) => {
                        return it.id == exclusion.id ? { ...exclusion, field: v?.value } : it
                      }),
                    )
                  }}
                  size="small"
                />
                <Autocomplete
                  value={getSelectedItem(
                    exclusion?.listItem?.id || null,
                    exclusion?.field?.list_id,
                  )}
                  id="autocomplete-field"
                  options={getListItemOptions(exclusion?.field)}
                  sx={{ minWidth: 160 }}
                  renderInput={(params) => <TextField {...params} label="Field Value" />}
                  onChange={(e, v) => {
                    setExclusions(
                      exclusions.map((it) => {
                        return it.id == exclusion.id ? { ...exclusion, listItem: v?.value } : it
                      }),
                    )
                  }}
                  size="small"
                />
                <IconButton
                  onClick={() => {
                    setExclusions(exclusions.filter((it) => it.id != exclusion.id))
                  }}
                >
                  <DeleteIcon size="small" />
                </IconButton>
              </div>
            )
          })}
        </>
      )}

      <AlternateComputationSetupUI
        altFieldTemplateId={altFieldTemplateId}
        setaltFieldTemplateId={setaltFieldTemplateId}
        fields={fields}
        activityTemplateId={activityTemplateId}
        field={field}
        listData={listData}
        lists={lists}
        altComputation={altComputation}
        setaltComputation={setaltComputation}
      />

      <div
        style={{
          display: 'flex',
          columnGap: 16,
          width: '100%',
          justifyContent: 'flex-end',
        }}
      >
        <div>
          <Button
            variant="outlined"
            color="primary"
            onClick={addAltComputation}
            style={{ marginTop: 16 }}
          >
            Add Alt Computation
          </Button>
        </div>

        <Button variant="outlined" color="primary" onClick={addParam} style={{ marginTop: 16 }}>
          Add Param
        </Button>

        {operation == COMPUTED_OPERATION_TYPES.SUM_ACROSS_PROCESS && (
          <Button
            variant="outlined"
            color="primary"
            size="small"
            onClick={addExclusion}
            style={{ marginTop: 16 }}
          >
            Add Exclusion
          </Button>
        )}
        <Button
          color="primary"
          variant="contained"
          size="small"
          onClick={saveComputation}
          style={{ marginTop: 16 }}
        >
          Save
        </Button>
      </div>
    </div>
  ) : (
    <div>
      <FormControl variant="outlined" size="small" color="primary" fullWidth>
        <InputLabel id="select-option-label">Is Computed</InputLabel>
        <Select
          style={{ minWidth: 60 }}
          size="small"
          color="primary"
          labelId="select-option-label"
          id="select-option"
          value={computed ? 'Yes' : 'No'}
          onChange={(e) => {
            setComputed(e.target.value == 'Yes')
          }}
          label="Polarity"
        >
          <MenuItem value={'No'}>No</MenuItem>
          <MenuItem value={'Yes'}>Yes</MenuItem>
        </Select>
      </FormControl>
      <div
        style={{
          display: 'flex',
          columnGap: 16,
          width: '100%',
          justifyContent: 'flex-end',
        }}
      >
        <Button
          color="primary"
          variant="contained"
          size="small"
          onClick={saveComputation}
          style={{ marginTop: 16 }}
        >
          Save
        </Button>
      </div>
    </div>
  )
}

export default FieldComputationSetupUI
